import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, branch } from 'recompose';
import { translator } from 'mage-i18n';
// eslint-disable-next-line import/no-extraneous-dependencies
import { BorderlessDropdown, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import {
  analytics,
  defaultContainerPropTypes,
  FieldsetForObject,
  formUtils,
  HorizontalWrapper,
  payloadUtils,
} from 'moet-core';

import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { CY_ENTITY_TYPE_SELECTION_CONTAINER_IDS } from '../constants';

import { validators } from '@/flow/validation';
import { shouldCollectDocuments } from '@/flow/configs/selectors/documents';
import { CAPS } from '@/constants';
import { withChainedCapabilitiesBeforeNext } from '@/flow/common';
import {
  getEntityTypesAvailableForMerchant,
  getMerchantAwareTranslator,
} from '@/selectors/merchant';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';

import { getDefaultState } from '../selectors';
import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.legal_entity_type';

export class EntityTypeSelection extends React.Component {
  constructor(props) {
    super(props);
    const { t, entityTypes, entityTypeKeys, isLoading = true, isDisabled } = props;
    const state = formUtils.initFormFromState({
      entityTypeKeys,
      fields: {
        entityType: {
          component: BorderlessDropdown,
          label: t(`${tPrefix}.fields.legal_entity_type.label`),
          value: EntityTypeSelection.getPrefillValue(props),
          isInitialized: false,
          isValid: false,
          validators: [
            {
              callback: validators.common.isOneOf,
              errorMessage: t(`${tPrefix}.fields.legal_entity_type.errors.is_one_of`),
              stateSelector: ({ entityTypeKeys = [] }) => ({
                subject: entityTypeKeys,
              }),
            },
          ],
          extraProps: {
            options: entityTypes,
            loading: isLoading,
            disabled: isDisabled,
            ...generateCyElemId(CY_ENTITY_TYPE_SELECTION_CONTAINER_IDS.INPUT_ENTITY_TYPE, window),
          },
        },
      },
    });
    this.state = {
      ...state,
      initialState: state,
    };
    this.onChangeFields = this.onChangeFields.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  static updateTranslation(key, props) {
    const { entityTypes = [], isLoading, isDisabled, t } = props;
    const fieldTranslationsMap = {
      entityType: {
        label: `${tPrefix}.fields.legal_entity_type.label`,
        validators: [
          {
            callback: validators.common.isOneOf,
            errorMessage: `${tPrefix}.fields.legal_entity_type.errors.is_one_of`,
            stateSelector: ({ entityTypeKeys = [] }) => ({
              subject: entityTypeKeys,
            }),
          },
        ],
        extraProps: {
          options: entityTypes,
          loading: isLoading,
          disabled: isDisabled,
          ...generateCyElemId(CY_ENTITY_TYPE_SELECTION_CONTAINER_IDS.INPUT_ENTITY_TYPE, window),
        },
      },
    };

    return formUtils.updateTranslatedFields(fieldTranslationsMap, key, t);
  }

  static getPrefillValue(props, currentStateValue = '') {
    const { defaultState: { entityType = '' } = {}, entityTypeKeys = [] } = props;
    const userHasAlreadyMadeAChange = currentStateValue !== '' && currentStateValue !== entityType;
    if (userHasAlreadyMadeAChange) {
      return currentStateValue;
    }
    return entityTypeKeys.includes(entityType) ? entityType : currentStateValue;
  }

  static getDerivedStateFromProps(props, state) {
    const { entityTypeKeys } = props;
    const newState = formUtils.initFormFromState({
      ...state,
      entityTypeKeys,
      fields: {
        ...state.fields,
        entityType: {
          ...state.fields.entityType,
          ...EntityTypeSelection.updateTranslation('entityType', props),
        },
      },
    });
    return {
      ...newState,
      initialState: state.initialState,
    };
  }

  onChangeFields(fields) {
    this.setState({ fields });
  }

  getBody(state) {
    return {
      entityType: state.fields.entityType.value,
    };
  }

  onNext() {
    this.props.onNext(
      payloadUtils.getMerchantCapabilityPayload(
        this.props.mid,
        this.state,
        this.getBody,
        'EntityTypeSelection',
      ),
    );
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t, tMerchantAware } = this.props;
    const { fields } = this.state;
    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical small />
        <FieldsetForObject
          name="entity-type"
          isDisabled={this.props.isDisabled}
          fields={fields}
          onBlur={key => formUtils.onBlurFields(key, this.state, this.onChangeFields)}
          onChange={(key, val) =>
            formUtils.onChangeFields(key, val, this.state, this.onChangeFields)
          }
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            loading={isLoading}
            onClick={() => {
              this.setState({
                ...this.state,
                fields: formUtils.initializeFields(this.state.fields),
              });
              formUtils.callToActionOnNextWrapper(this.state.fields, this.onNext);
            }}
            disabled={isDisabled}
            {...generateCyElemId(CY_ENTITY_TYPE_SELECTION_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

EntityTypeSelection.propTypes = {
  ...defaultContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

const WrappedComponent = compose(
  connect((state, { country }) => {
    const entityTypes = getEntityTypesAvailableForMerchant(state);
    return {
      entityTypes,
      entityTypeKeys: formUtils.getOptionKeys(entityTypes),
      defaultState: getDefaultState(state),
      t: translator(state),
      tMerchantAware: getMerchantAwareTranslator(state),
      countryCollectsDocuments: shouldCollectDocuments(state),
    };
  }),
  branch(
    ({ countryCollectsDocuments }) => countryCollectsDocuments,
    withChainedCapabilitiesBeforeNext(
      {
        pathBusinessDetails: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH,
        refreshDocuments: CAPS.V2_DOC_LIST,
      },
      {
        forceCapabilitiesOrder: ['pathBusinessDetails', 'refreshDocuments'],
        callback: ({ payload }) =>
          analytics.logEvent(ANALYTICS_EVENTS.selectEntityType, payload.body),
      },
    ),
    withChainedCapabilitiesBeforeNext(
      {
        pathBusinessDetails: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH,
      },
      {
        forceCapabilitiesOrder: ['pathBusinessDetails'],
        callback: ({ payload }) =>
          analytics.logEvent(ANALYTICS_EVENTS.selectEntityType, payload.body),
      },
    ),
  ),
)(EntityTypeSelection);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.deploymentRegionsAndCountries = {
  eu: ['fallback'],
  ap: ['fallback'],
  us: ['fallback'],
};

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    fields: {
      entityType: {},
    },
  },
];

export default WrappedComponent;
